import { Box, Container, Divider, Typography } from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import AlertDialog, { AlertDialogProps } from '@/components/alert-dialog'
import Button from '@/components/button'
import Loading from '@/components/loading'
import { useCompanies } from '@/hooks/use-companies'
import {
  DefaultSelectFilesFormData,
  useSelectFilesForm,
} from '@/pages/demands/steps/select-files/hooks/use-select-files-form'
import { routes } from '@/routes/config'

import { useDiagnosisRegistrationStore } from '../../contexts/diagnosis-registration-store-context'
import { CustomDropzone } from './components/custom-dropzone'
import { useStyles } from './styles'

type AlertTypes = Omit<AlertDialogProps, 'open'>

export const SelectFiles = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const { permissions } = useCompanies()

  const { registerData, selectFilesData, setSelectFilesData, setPolygonsData } =
    useDiagnosisRegistrationStore((state) => ({
      registerData: state.registerData,
      selectFilesData: state.selectFilesData,
      setSelectFilesData: state.setSelectFilesData,
      setPolygonsData: state.setPolygonsData,
    }))

  const [modalStates, setModalStates] = useState({
    open: false,
    inputBase: '' as 'rgb' | 'thermal',
  })

  const {
    control,
    register,
    unregister,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { isSubmitting, isValidating, errors },
  } = useSelectFilesForm({
    defaultValues: selectFilesData || undefined,
  })
  const imageType = registerData?.image_type
  const isPhysiological = !permissions?.WeedAccess
  const errorPerimeterType = errors.perimeter?.results?.area?.type

  function onSubmit(data: DefaultSelectFilesFormData) {
    setPolygonsData(null)
    setSelectFilesData(data)
    navigate(routes.Demands('resume'))
  }

  function syncInputsByImageType() {
    register('perimeter.results')

    if (imageType === 'mosaic') {
      return register('mosaic.results')
    }

    if (imageType === 'raw_images_geotag' || imageType === 'raw_images') {
      if (isPhysiological) {
        register('thermal.results')
      }
      return register('rgb.results')
    }
  }

  useEffect(() => {
    syncInputsByImageType()
  }, [register, unregister])

  const currentInputResults = watch(`${modalStates.inputBase}.results`)

  function getAlertProps(): AlertTypes {
    if (errors[modalStates.inputBase]?.input) {
      return {
        variant: 'error',
        title: 'Erro no recebimento das imagens',
        subtitle: errors[modalStates.inputBase]!.input!.message!,
        icon: <ReportProblemOutlinedIcon />,
        confirm: 'Ok',
        handleConfirm: () => {
          handleClose()
          setValue(`${modalStates.inputBase}.input`, [])
        },
      }
    }
    if (currentInputResults) {
      return {
        variant: 'success',
        title: 'Análise concluída',
        subtitle: 'As imagens foram analisadas com sucesso.',
        icon: <CheckCircleOutlineOutlinedIcon />,
        autoClose: true,
        handleConfirm: () => {
          handleClose()
        },
      }
    }
    return {
      variant: 'success',
      title: '',
      subtitle: 'Estamos analisando as imagens selecionadas.',
      icon: <Loading />,
      loading: true,
    }
  }

  function handleClose() {
    setModalStates({ open: false, inputBase: '' as any })
  }

  if (!imageType) return <Navigate to={routes.Demands('register')} replace />

  return (
    <Container
      className={classes.main}
      component='form'
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Typography variant={'h1'}>
          Seleção de arquivos de processamento
        </Typography>
        <Typography variant={'h4'}>Arquivos de processamento</Typography>

        <Box className={classes.container}>
          {(imageType === 'raw_images_geotag' ||
            imageType === 'raw_images') && (
            <>
              <CustomDropzone
                control={control}
                name='rgb.input'
                label={
                  isPhysiological
                    ? 'Imagens brutas - .jpg (até 5.000 imagens)'
                    : 'Imagens brutas - .jpg (até 10.000 imagens)'
                }
                labelViewImagesCount
                accept={{
                  'image/jpg, image/jpeg': ['.jpg', '.jpeg'],
                }}
                onDrop={() => setModalStates({ open: true, inputBase: 'rgb' })}
              />
              {isPhysiological && (
                <CustomDropzone
                  control={control}
                  name='thermal.input'
                  label='Imagens térmicas - .jpg (até 5.000 imagens)'
                  labelViewImagesCount
                  accept={{
                    'image/jpg, image/jpeg': ['.jpg', '.jpeg'],
                  }}
                  onDrop={() =>
                    setModalStates({ open: true, inputBase: 'thermal' })
                  }
                />
              )}

              <AlertDialog {...getAlertProps()} open={modalStates.open} />
            </>
          )}

          {imageType === 'raw_images_geotag' && (
            <CustomDropzone
              control={control}
              name='geotag.input'
              label='Geotag - .txt'
              accept={{ 'text/plain': ['.txt'] }}
            />
          )}

          {imageType === 'mosaic' && (
            <CustomDropzone
              control={control}
              name='mosaic.input'
              label='Ortomosaico - .tif'
              accept={{
                'image/tiff': ['.tif'],
              }}
              errors={[
                errors.mosaic?.results?.message,
                errors.mosaic?.results?.gsd?.message,
                errors.mosaic?.results?.coord_x?.message,
                errors.mosaic?.results?.coord_y?.message,
                errors.mosaic?.results?.px_res?.message,
              ]}
              maxFiles={1}
            />
          )}

          <CustomDropzone
            control={control}
            name='perimeter.input'
            label='Perímetro - .shp + .shx + .dbf + .prj + .cpg '
            accept={{
              'application/octet-stream': [
                '.shp',
                '.shx',
                '.dbf',
                '.prj',
                '.cpg',
              ],
            }}
            errors={[
              errors.perimeter?.results?.message,
              errors.perimeter?.results?.filename?.message,
              errors.perimeter?.results?.geojson?.message,
              errors.perimeter?.results?.area?.message,
            ]}
          />
          <AlertDialog
            variant='error'
            title='Ocorreu um erro'
            subtitle='É necessário corrigir o arquivo de perímetro.'
            items={[
              'Erro de Geometria: Orientação Invertida.',
              ' Seu arquivo possui um erro de geometria. Para corrigi-lo, basta exportar o arquivo utilizando o QGIS. O ajuste será automático.',
            ]}
            icon={<InfoOutlined />}
            open={errorPerimeterType === 'too_small'}
            handleConfirm={() => setError('perimeter.results.area', {})}
            confirm='Entendi'
          />
        </Box>
      </Box>
      <Box className={classes.buttonContainer}>
        <Divider />
        <Box>
          <Button outlined onClick={() => navigate(-1)}>
            Cancelar
          </Button>
          <Button type='submit' loading={isValidating || isSubmitting}>
            Avançar
          </Button>
        </Box>
      </Box>
    </Container>
  )
}
